<script lang="ts">
  import UserCircleCheck from "~icons/ph/user-circle-check";
  import Check from "~icons/ph/check";
  import WaitButton from "../../components/WaitButton.svelte";
  import { onMount } from "svelte";

  interface Address {
    address_id: string | null;
    line1: string;
    line2: string;
    line3: string;
    line4: string;
    city: string;
    postcode: string;
    full_address: string;
  }

  let {
    address = $bindable(),
    back,
  }: {
    address: {};
    back: () => void;
  } = $props();

  let input = $state<HTMLInputElement>()
  let value = $state("")
  let found = $state<Address[]>([])
  let error = $state<string | null>(null)
  let selected = $state<Address | null>(null)
  let wait: boolean = $state(false)

  function reset(options = {}){
    const state = {
      value: value,
      found: [],
      error: null,
      selected: null,
      wait: false,
      ...options
    };
    ({value, found, error, selected, wait} = state)
  }

  function search(event){
    event.preventDefault();
    wait = true;
    getSuggestions(value);
  }

  async function getSuggestions(value){
    try {
      const response = await fetch(`/postcode_addresses/${encodeURIComponent(value)}`);
      const data = await response.json();
      if (data.error) return reset({ error: data.error });
      reset({ found: data.addresses });
      input.blur()
    } catch {
      reset({ error: "Something unexpected went wrong. Please try again." });
    }
  }

  async function confirm(event) {
    event.preventDefault();
    address = {
      street: [selected.line1, selected.line2, selected.line3, selected.line4].filter(Boolean).join("\n"),
      city: selected.city,
      postal_code: selected.postcode,
      country: "United Kingdom"
    };
    back();
  }

  onMount(()=>input.focus());
</script>

<h2 class="ui-title">Update Postal Address</h2>

<form onsubmit={search}>
  <fieldset class="ui-fieldset">
    <div class="join w-full">
      <input
        class="ui-input join-item uppercase placeholder:normal-case"
        type="text"
        placeholder="Enter postcode"
        minlength="5"
        required
        bind:this={input}
        bind:value
      >
      <WaitButton class="join-item btn btn-primary" {wait}>Search</WaitButton>
    </div>

    {#if error}<div class="ui-error">{error}</div>{/if}

    <div class="ui-alert ui-alert-info">
      <UserCircleCheck />
      <p>For your security, we’ll ask for <b>ID with proof of address</b> afterwards to verify changes.</p>
    </div>
  </fieldset>
</form>

<form onsubmit={confirm}>
  {#if found.length}
    <h3 class="ui-section-title">Results</h3>

    <fieldset class="flex flex-col gap-4">
      {#each found as suggestion}
        {@const itsme = selected === suggestion}
        <label class="flex justify-between items-center gap-2 rounded py-2 px-3 text-[15px] leading-tight
                      border focus-within:outline-none focus-within:border-blue-600
                      {itsme ? "border-blue-600 bg-emerald-200" : "border-neutral-300"}"
        >
          {suggestion.full_address}
          <input
            type="radio"
            name="address"
            checked={itsme}
            onchange={()=> selected = suggestion}
            class="sr-only"
          />
          <div class="h-[18px] w-[18px] shrink-0 border {itsme ? "border-emerald-500 bg-emerald-500" : "border-neutral-300" }
                      rounded-full flex items-center justify-center text-white">
            {#if itsme}<Check class="h-3 w-3 shrink-0" />{/if}
          </div>
        </label>
      {/each}
    </fieldset>
  {/if}

  <div class="floating-footer">
    <button class="btn btn-ghost" type="button" onclick={back}>Back</button>
    <button class="btn btn-primary" type="submit" disabled={!selected}>Continue</button>
  </div>
</form>

<style>
  /* This style overwrite can be removed once the task design is applied more globally */
  button:disabled {
    opacity: 50%;
    color: white;
    background-color: oklch(57.1889% 0.225412 259.339943 / 1);
  }
</style>
